// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)

import React, { useCallback, useEffect, useMemo } from "react";
import { Loader } from "semantic-ui-react";
//Weather Data elements are the only ones on this page to use lodash
import _ from "lodash";

import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import * as PropTypes from "prop-types";
import { getAnalysisResultsForTrialTimecourse } from "../../../../apis/analysisApi";
import { fetchTrialData } from "../../../../apis/trialApi";
import { validateWeatherDataAsync } from "../../../../apis/weatherDataApi";
import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import TimecourseComponentNew from "../../../Lumber/Timecourse/TimecourseComponentNew";
import { useTrialAnalysis, useTrialAnalysisDispatch } from "../../Trials/TrialAnalysis/TrialAnalysisContext";
import { getIsPlotUniformity } from "../../Trials/TrialAnalysis/utils";

const Timecourse = ({ trialOptions }) => {
	const moduleNavigation = useModuleNavigation();
	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();

	const { display, committed, pending, isInitialized } = useTrialAnalysis();
	const { initializeSettings, changeDisplaySettings } = useTrialAnalysisDispatch();
	const queryClient = useQueryClient();

	const weatherDataEnabled = true;

	const isPlotUniformity = useMemo(
		() => getIsPlotUniformity(trialOptions, pending.analysisTypeId),
		[trialOptions, pending.analysisTypeId],
	);

	const analysisQueryKeyBase = useMemo(
		() => [
			"analysis data trial timecourse",
			userAuth.currentClient,
			moduleNavigation.trialId,
			pending.analysisId,
			pending.quantifiedRegionTypeId,
			isPlotUniformity,
			committed.removeDamagedOrExcluded,
			committed.useGdu,
		],
		[
			userAuth.currentClient,
			moduleNavigation.trialId,
			pending.analysisId,
			pending.quantifiedRegionTypeId,
			isPlotUniformity,
			committed.removeDamagedOrExcluded,
			committed.useGdu,
		],
	);

	//-- Data Queries
	const { data: trialData, isSuccess: isSuccessTrialData } = useQuery({
		queryKey: ["fetch trial data", userAuth.currentClientId, moduleNavigation.trialId],
		queryFn: async () => fetchTrialData(userAuth.currentClientId, moduleNavigation.trialId, await getTokenSilently()),
		select: useCallback(
			(data) => {
				if (!display.treatmentNames || display.treatmentNames.length === 0) return data;
				return {
					...data,
					treatments: _.map(display.treatmentNames, (tn, idx) => ({
						treatmentId: _.uniqueId(),
						trialTreatmentId: idx,
						treatmentName: tn,
						treatmentDescription: null,
						treatmentTypeId: null,
						treatmentTypeName: null,
						treatmentTypeDescription: null,
						seed: null,
					})),
				};
			},
			[display.treatmentNames],
		),
		enabled: !!userAuth.currentClient && !!moduleNavigation.trialId,
		staleTime: Infinity,
		placeholderData: keepPreviousData,
	});

	const { data: gduByDay } = useQuery({
		queryKey: ["get gdu for trial", moduleNavigation.trialId, userAuth.currentClientId],
		queryFn: async () =>
			validateWeatherDataAsync(moduleNavigation.trialId, userAuth.currentClientId, true, await getTokenSilently()),
		enabled:
			!!userAuth.currentClient && !!moduleNavigation.trialId && weatherDataEnabled && !!trialData?.trialInfo?.plantDate,
		staleTime: Infinity,
		placeholderData: keepPreviousData,
	});

	const {
		data: analysisData,
		isFetching,
		isSuccess,
	} = useQuery({
		queryKey: [...analysisQueryKeyBase, committed.selectedFlightIds],
		queryFn: async () =>
			getAnalysisResultsForTrialTimecourse(
				userAuth.currentClientId,
				moduleNavigation.trialId,
				pending.analysisId,
				pending.quantifiedRegionTypeId,
				isPlotUniformity,
				committed.removeDamagedOrExcluded,
				committed.selectedFlightIds,
				trialData.trialInfo.plantDate,
				trialData.trialInfo.harvestDate,
				committed.useGdu,
				await getTokenSilently(),
			),
		select: useCallback(
			(data) => {
				const dataset = display.experimentalDesignId
					? data?.data.designResultsDict[display.experimentalDesignId]
					: _.values(data?.data.designResultsDict)?.[0];

				//-- REMOVE STAND COUNT DATA FROM TIMECOURSE PAGES
				const filteredDataset = _.filter(dataset, (par) => !par.analysisName.includes("Stand Count"));

				const aucData = display.experimentalDesignId
					? data?.data.aucDict[display.experimentalDesignId]
					: _.values(data?.data.aucDict)?.[0];

				const defaultFlightIds = _.uniq(_.map(filteredDataset, "flightId"));
				const defaultTreatmentMatrix = !_.every(aucData, (ad) => ad.trialTreatmentId === 0)
					? _.reduce(
							aucData,
							(acc, curr) => {
								acc[curr.trialTreatmentId] = curr.trialTreatmentId !== 0 ? true : false;
								return acc;
							},
							[],
						)
					: [true];

				return {
					dataset: filteredDataset,
					aucData,
					plantFlightId: data?.data.plantFlightId,
					harvestFlightId: data?.data.harvestFlightId,
					defaultFlightIds,
					defaultTreatmentMatrix,
				};
			},
			[display.experimentalDesignId],
		),
		enabled:
			!!userAuth.currentClient &&
			!!moduleNavigation.trialId &&
			isInitialized &&
			!!committed.analysisId &&
			isSuccessTrialData,
		staleTime: Infinity,
		placeholderData: keepPreviousData,
	});

	//-- Data Wrangling
	// TODO: is this needed still?
	const timecourseData = useMemo(() => {
		if (!analysisData || !isSuccess || !trialData) return [];
		let plotAnalysisResults = _.cloneDeep(analysisData.dataset);

		if (weatherDataEnabled && trialData.trialInfo.plantDate) {
			plotAnalysisResults = _.filter(plotAnalysisResults, (r) => r.curveModelDataAnalysisId === null);
		} else {
			plotAnalysisResults = _.filter(
				plotAnalysisResults,
				(r) => r.curveModelDataAnalysisId === null && r.analysisId !== null,
			);
		}
		return plotAnalysisResults;
	}, [analysisData, isSuccess, trialData, weatherDataEnabled]);

	// First load: selectedFlightIds (committed) is null until this runs.
	useEffect(() => {
		if (
			committed.selectedFlightIds !== null ||
			!analysisData?.defaultFlightIds ||
			!analysisData?.defaultTreatmentMatrix
		)
			return;

		// Seed cache for the explicit-all-flights key so committing selectedFlightIds
		// (null → defaultFlightIds) doesn't trigger a redundant re-fetch.
		const nullKeyData = queryClient.getQueryData([...analysisQueryKeyBase, null]);
		if (
			nullKeyData !== undefined &&
			queryClient.getQueryData([...analysisQueryKeyBase, analysisData.defaultFlightIds]) === undefined
		) {
			queryClient.setQueryData([...analysisQueryKeyBase, analysisData.defaultFlightIds], nullKeyData);
		}

		initializeSettings({
			selectedTreatmentMatrix: analysisData.defaultTreatmentMatrix,
			selectedFlightIds: analysisData.defaultFlightIds,
		});
	}, [analysisData, analysisQueryKeyBase, initializeSettings, queryClient, committed.selectedFlightIds]);

	useEffect(() => {
		if (!analysisData?.defaultFlightIds) return;
		changeDisplaySettings({ assessmentFlightIds: analysisData.defaultFlightIds });
	}, [analysisData?.defaultFlightIds, changeDisplaySettings]);

	// Design change: re-initialize defaults when experimentalDesignId changes after first load.
	// analysisData and selectedFlightIds are intentionally omitted — select() re-runs synchronously
	// during the render that triggers this effect, so the closure captures current values.
	useEffect(() => {
		if (
			committed.selectedFlightIds === null ||
			!analysisData?.defaultFlightIds ||
			!analysisData?.defaultTreatmentMatrix
		)
			return;

		initializeSettings({
			selectedTreatmentMatrix: analysisData.defaultTreatmentMatrix,
			selectedFlightIds: analysisData.defaultFlightIds,
		});
	}, [display.experimentalDesignId, initializeSettings]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (gduByDay?.length) {
			changeDisplaySettings({ hasGdus: true });
		} else {
			changeDisplaySettings({ hasGdus: false });
		}
	}, [gduByDay, changeDisplaySettings]);

	return (
		<>
			{timecourseData && trialData ? (
				<TimecourseComponentNew
					data={timecourseData}
					analysisData={analysisData}
					dataLoading={isFetching}
					trialDataResults={trialData}
					trialOptions={trialOptions}
					gduList={gduByDay}
				/>
			) : (
				<div style={{ width: "100%", flexGrow: 1 }}>
					<Loader inline="centered" active />
				</div>
			)}
		</>
	);
};

Timecourse.propTypes = {
	trialOptions: PropTypes.object,
};

export default Timecourse;
